/**
 * 🎨 TWO FACTOR INPUT
 * 
 * Componente de input para código de 6 dígitos do 2FA
 * 
 * Features:
 * - 6 inputs separados (1 dígito cada)
 * - Auto-focus no próximo ao digitar
 * - Backspace volta para anterior
 * - Suporte a colar código completo
 * - Validação visual (verde/vermelho)
 * - Teclado numérico no mobile
 * 
 * @component
 */

import React, { useRef, useState, useEffect } from 'react';
import './TwoFactorInput.scss';

const TwoFactorInput = ({ value, onChange, onComplete, error, disabled }) => {
  const inputsRef = useRef([]);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);

  // Atualizar digits quando value mudar (controlado)
  useEffect(() => {
    if (value && value.length <= 6) {
      const newDigits = value.split('').concat(Array(6).fill('')).slice(0, 6);
      setDigits(newDigits);
    }
  }, [value]);

  /**
   * Handler de mudança de dígito
   */
  const handleChange = (index, newValue) => {
    // Permitir apenas números
    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);

    const fullCode = newDigits.join('').trim(); // ✅ TRIM para remover espaços
    onChange(fullCode);

    // Auto-focus no próximo input
    if (newValue && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }

    // Chamar onComplete se completou todos os 6 dígitos
    if (fullCode.length === 6 && !fullCode.includes('')) {
      onComplete?.(fullCode);
    }
  };

  /**
   * Handler de teclas especiais
   */
  const handleKeyDown = (index, e) => {
    // Backspace: limpar atual e voltar pro anterior
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        // Se atual está vazio, volta pro anterior
        inputsRef.current[index - 1]?.focus();
      } else {
        // Limpa o atual
        const newDigits = [...digits];
        newDigits[index] = '';
        setDigits(newDigits);
        onChange(newDigits.join(''));
      }
    }

    // Seta esquerda
    if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }

    // Seta direita
    if (e.key === 'ArrowRight' && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  /**
   * Handler de colar (paste)
   */
  const handlePaste = (e) => {
    e.preventDefault();
    
    const pastedText = e.clipboardData.getData('text/plain').trim();
    
    // Validar se é um código válido (6 dígitos numéricos)
    if (!/^\d{6}$/.test(pastedText)) {
      return;
    }

    const newDigits = pastedText.split('');
    setDigits(newDigits);
    onChange(pastedText);

    // Focar no último input
    inputsRef.current[5]?.focus();

    // Chamar onComplete
    onComplete?.(pastedText);
  };

  /**
   * Handler de foco
   */
  const handleFocus = (index) => {
    // Selecionar todo o conteúdo ao focar
    inputsRef.current[index]?.select();
  };

  /**
   * Classe CSS baseada no estado
   */
  const getInputClass = (index) => {
    const classes = ['two-factor-input__digit'];
    
    if (digits[index]) {
      classes.push('two-factor-input__digit--filled');
    }
    
    if (error) {
      classes.push('two-factor-input__digit--error');
    }
    
    if (disabled) {
      classes.push('two-factor-input__digit--disabled');
    }
    
    return classes.join(' ');
  };

  return (
    <div className="two-factor-input">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => (inputsRef.current[index] = el)}
          type="text"
          maxLength={1}
          pattern="[0-9]"
          inputMode="numeric"
          value={digits[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          className={getInputClass(index)}
          disabled={disabled}
          autoFocus={index === 0}
          aria-label={`Dígito ${index + 1}`}
        />
      ))}
    </div>
  );
};

export default TwoFactorInput;

